import React, { useState, useEffect } from 'react';
import './TimelinePlayer.css';

/**
 * TimelinePlayer - Controls time-based animation of stock movements
 * Shows play/pause controls and timeline scrubber
 * Auto-starts animation on load
 */
const TimelinePlayer = ({ totalSteps, currentStep, onStepChange, stocks }) => {
  const [isPlaying, setIsPlaying] = useState(true); // Auto-start animation
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Auto-advance timeline when playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      onStepChange((prev) => (prev + 1) % totalSteps);
    }, 500 / playbackSpeed); // Adjust speed with playback multiplier

    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed, totalSteps, onStepChange]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    onStepChange(0);
  };

  const handleSliderChange = (e) => {
    onStepChange(parseInt(e.target.value));
    setIsPlaying(false);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
  };

  // Calculate date for display
  const getDateAtStep = (step) => {
    const today = new Date();
    const daysBack = totalSteps - 1 - step;
    const date = new Date(today);
    date.setDate(date.getDate() - daysBack);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="timeline-player">
      <div className="player-controls">
        {/* Play/Pause Button */}
        <button
          className="player-btn play-btn"
          onClick={togglePlay}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>

        {/* Reset Button */}
        <button
          className="player-btn reset-btn"
          onClick={handleReset}
          title="Reset to start"
        >
          ⏮
        </button>

        {/* Timeline Scrubber */}
        <div className="timeline-scrubber">
          <input
            type="range"
            min="0"
            max={totalSteps - 1}
            value={currentStep}
            onChange={handleSliderChange}
            className="timeline-slider"
            title="Scrub through time"
          />
          <div className="timeline-labels">
            <span className="timeline-start">
              {getDateAtStep(0)}
            </span>
            <span className="timeline-current">
              {getDateAtStep(currentStep)}
            </span>
            <span className="timeline-end">
              {getDateAtStep(totalSteps - 1)}
            </span>
          </div>
        </div>

        {/* Speed Controls */}
        <div className="speed-controls">
          {[1, 2, 5].map((speed) => (
            <button
              key={speed}
              className={`speed-btn ${playbackSpeed === speed ? 'active' : ''}`}
              onClick={() => handleSpeedChange(speed)}
              title={`${speed}x speed`}
            >
              {speed}x
            </button>
          ))}
        </div>

        {/* Step Counter */}
        <div className="step-counter">
          {currentStep + 1} / {totalSteps}
        </div>
      </div>
    </div>
  );
};

export default TimelinePlayer;
